import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Flex, Modal, Progress, Spin, Tag, Tooltip, Upload } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadProps } from "antd";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createSupplierQuotationRequest,
  fetchQuotationRequests,
  QuotationRequestInts,
  QuotationRequestsParams
} from "@/apis/requirement";
import { list as fetchSuppliers } from "@/apis/supplier";
import { CommonStateEnum } from "@/types/common";
import {
  QuotationRequestStatusEnum,
  Requirement
} from "@/types/requirement";
import { RequirementDetailType } from "../../hooks/useGetDetail";
import {
  parseQuotationImportFile,
  RawQuotationImportRow
} from "./utils";
import CommonTable from "@/components/CommonTable";

type ImportPhase = "preview" | "submitting" | "completed";
type SubmitStatus = "pending" | "success" | "failed";

interface QuotationImportRow extends RawQuotationImportRow {
  key: string;
  requirementItem?: RequirementDetailType;
  priceValue?: number;
  mpqValue?: string;
  errors: string[];
  submitStatus?: SubmitStatus;
  submitMessage?: string;
  remark: string;
}

interface ImportQuotationExcelProps {
  requirement: Requirement;
  items: RequirementDetailType[];
  onSuccess: () => void;
}

const ImportQuotationExcel: FC<ImportQuotationExcelProps> = ({
  requirement,
  items,
  onSuccess
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<ImportPhase>("preview");
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [importError, setImportError] = useState("");
  const [rows, setRows] = useState<QuotationImportRow[]>([]);

  const hasErrors = rows.some(row => row.errors.length > 0);

  const summary = useMemo(() => rows.reduce(
    (result, row) => {
      if (row.submitStatus === "success") result.success += 1;
      if (row.submitStatus === "failed") result.failed += 1;
      return result;
    },
    { success: 0, failed: 0 }
  ), [rows]);

  // 批量校验需求项、输入格式和启用供应商编码；供应商价格关系仍由后端处理。
  const resolveRows = async (rawRows: RawQuotationImportRow[]): Promise<QuotationImportRow[]> => {
    const itemMap = new Map(items.map(item => [item.id, item]));
    const supplierCodes = Array.from(new Set(
      rawRows.map(row => row.supplierCode).filter(Boolean)
    ));
    const activeSupplierCodes = new Set<string>();
    let supplierLoadFailed = false;

    if (supplierCodes.length > 0) {
      try {
        const supplierData = await fetchSuppliers(
          { strs: supplierCodes },
          CommonStateEnum.ACTIVE
        );
        (supplierData.body || []).forEach(supplier => {
          if (supplier.code) activeSupplierCodes.add(supplier.code.trim());
        });
      } catch {
        supplierLoadFailed = true;
      }
    }

    return rawRows.map(row => {
      const errors: string[] = [];
      const requirementItem = itemMap.get(row.requirementItemId);
      const priceValue = Number(row.supplierPrice);
      const mpqNumber = row.mpq === "" ? null : Number(row.mpq);

      if (!row.requirementItemId) {
        errors.push(t("requirement.quotationImportItemIdRequired"));
      } else if (!requirementItem) {
        errors.push(t("requirement.quotationImportItemNotFound"));
      }

      if (
        requirementItem &&
        (requirementItem.product?.code ?? "") !== row.productCode
      ) {
        errors.push(t("requirement.quotationImportProductMismatch"));
      }

      if (!row.supplierCode) {
        errors.push(t("requirement.quotationImportSupplierRequired"));
      } else if (supplierLoadFailed) {
        errors.push(t("requirement.quotationImportSupplierLoadFailed"));
      } else if (!activeSupplierCodes.has(row.supplierCode)) {
        errors.push(`${t("purchase.supplierNotExisted")}: ${row.supplierCode}`);
      }
      if (!row.supplierPrice || !Number.isFinite(priceValue) || priceValue <= 0) {
        errors.push(t("requirement.quotationImportPriceInvalid"));
      }
      if (mpqNumber !== null && (!Number.isFinite(mpqNumber) || mpqNumber <= 0)) {
        errors.push(t("requirement.quotationImportMpqInvalid"));
      }
      if (!requirement.mrd) {
        errors.push(t("requirement.quotationImportNoMrd"));
      }

      return {
        ...row,
        key: `${row.rowNumber}-${row.requirementItemId}-${row.supplierCode}`,
        requirementItem,
        priceValue: Number.isFinite(priceValue) ? priceValue : undefined,
        mpqValue: row.mpq || undefined,
        errors
      };
    });
  };

  // Ant Design Upload 只负责选择文件，文件解析和预览由前端完成，不直接上传原文件。
  const handleImport: UploadProps["beforeUpload"] = async file => {
    setRows([]);
    setFileName(file.name);
    setImportError("");
    setProgress(0);
    setPhase("preview");
    setOpen(true);
    setLoading(true);
    try {
      const rawRows = await parseQuotationImportFile(
        file,
        {
          requirementItemId: t("requirement.quotationImportRequirementItemId"),
          productCode: t("product.code"),
          productName: t("product.name"),
          supplierCode: t("requirement.quotationImportSupplierCode"),
          supplierName: t("requirement.quotationImportSupplierName"),
          supplierPrice: t("requirement.quotationImportPrice"),
          mpq: t("requirement.quotationImportMpq"),
          remark: t("purchase.orderDesc"),
        }
      );
      const resolvedRows = await resolveRows(rawRows);
      setRows(resolvedRows);
    } catch (error) {
      const message = error instanceof Error && error.message === "invalid-header"
        ? t("requirement.quotationImportInvalidHeader")
        : error instanceof Error && error.message === "empty-file"
          ? t("requirement.quotationImportFileEmpty")
          : t("requirement.quotationImportParseFailed");
      setImportError(message);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const updateSubmitStatus = (
    key: string,
    submitStatus: SubmitStatus,
    submitMessage?: string
  ) => {
    setRows(current => current.map(row => (
      row.key === key ? { ...row, submitStatus, submitMessage } : row
    )));
  };

  const getNextDeadline = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime();
  };

  // 按 Excel 行顺序逐条创建报价，单行失败不会中断后续行。
  const handleSubmit = async () => {
    if (rows.length === 0 || hasErrors || !requirement.mrd) return;

    const submitRows = rows.map(row => ({
      ...row,
      submitStatus: "pending" as SubmitStatus,
      submitMessage: undefined
    }));
    setRows(submitRows);
    setProgress(0);
    setPhase("submitting");

    const ddlMap = new Map<string, number | null>();
    let successCount = 0;

    for (let index = 0; index < submitRows.length; index += 1) {
      const row = submitRows[index];
      try {
        const requirementItemId = row.requirementItem!.id;
        let ddl = ddlMap.get(requirementItemId);
        if (ddl === undefined) {
          // 同一需求明细复用已有手动报价的截止时间，避免每个供应商重复查询。
          try {
            const quotationRequests = await fetchQuotationRequests(
              requirementItemId,
              [QuotationRequestInts.MANUAL]
            );
            ddl = quotationRequests[0]?.deadline ?? null;
          } catch {
            ddl = null;
          }
          ddlMap.set(requirementItemId, ddl);
        }

        const payload: QuotationRequestsParams = {
          deadline: getNextDeadline(),
          mrd: requirement.mrd,
          supplierPrice: row.priceValue!,
          supplierTime: requirement.mrd,
          mpq: row.mpqValue ?? "",
          ddl,
          // Excel 不提供以下价格派生字段，批量报价统一使用默认值。
          days: 0,
          spareCount: "0",
          spareRate: "0",
          remark: row.remark ?? ''
        };
        // 接口路径中的 code 直接使用当前 Excel 行的供应商编码。
        await createSupplierQuotationRequest(
          requirementItemId,
          row.supplierCode,
          payload,
          QuotationRequestStatusEnum.SUBMITED
        );

        successCount += 1;
        updateSubmitStatus(row.key, "success");
      } catch {
        updateSubmitStatus(
          row.key,
          "failed",
          t("requirement.quotationImportSubmitFailed")
        );
      } finally {
        setProgress(Math.round(((index + 1) / submitRows.length) * 100));
      }
    }

    setPhase("completed");
    if (successCount > 0) onSuccess();
  };

  const handleClose = () => {
    if (phase === "submitting" || loading) return;
    setOpen(false);
    setRows([]);
    setFileName("");
    setImportError("");
    setProgress(0);
    setPhase("preview");
  };

  const getStatusContent = (row: QuotationImportRow) => {
    if (row.errors.length > 0) {
      return (
        <Tag color="red" style={{ whiteSpace: "normal" }}>
          {row.errors.join("；")}
        </Tag>
      );
    }
    if (row.submitStatus === "success") {
      return <Tag color="green">{t("requirement.success")}</Tag>;
    }
    if (row.submitStatus === "failed") {
      return (
        <Tooltip title={row.submitMessage}>
          <Tag color="red">{t("requirement.failed")}</Tag>
        </Tooltip>
      );
    }
    if (row.submitStatus === "pending") {
      return <Tag color="blue">{t("requirement.pending")}</Tag>;
    }
    return <Tag color="green">{t("requirement.quotationImportReady")}</Tag>;
  };

  const columns: ColumnsType<QuotationImportRow> = [
    {
      title: t("requirement.quotationImportRowNumber"),
      dataIndex: "rowNumber",
      width: 60,
      fixed: "left"
    },
    {
      title: t("requirement.quotationImportRequirementItemId"),
      dataIndex: "requirementItemId",
      width: 220,
      ellipsis: true
    },
    {
      title: t("product.code"),
      dataIndex: "productCode",
      width: 150,
      ellipsis: true
    },
    {
      title: t("product.name"),
      dataIndex: "productName",
      width: 160,
      ellipsis: true
    },
    {
      title: t("requirement.quotationImportSupplierCode"),
      dataIndex: "supplierCode",
      width: 150,
      ellipsis: true
    },
    {
      title: t("requirement.quotationImportSupplierName"),
      dataIndex: "supplierName",
      width: 170,
      ellipsis: true
    },
    {
      title: t("requirement.quotationImportPrice"),
      dataIndex: "supplierPrice",
      width: 110
    },
    {
      title: t("requirement.quotationImportMpq"),
      dataIndex: "mpq",
      width: 110
    },
    {
      title: t("purchase.orderDesc"),
      dataIndex: "remark",
      width: 110
    },
    {
      title: t("requirement.quotationImportStatus"),
      key: "status",
      fixed: "right",
      width: 240,
      render: (_, row) => getStatusContent(row)
    }
  ];

  return (
    <>
      <Upload
        accept=".xlsx,.xls"
        beforeUpload={handleImport}
        showUploadList={false}
        disabled={loading}
      >
        <Button type="link" icon={<UploadOutlined />}>
          {t("requirement.importQuotationExcel")}
        </Button>
      </Upload>

      <Modal
        title={`${t("requirement.quotationImportPreview")}：${fileName}`}
        open={open}
        width={1000}
        centered
        maskClosable={false}
        closable={phase !== "submitting" && !loading}
        onCancel={handleClose}
        footer={loading ? null : phase === "preview" ? (
          <Flex justify="end" gap={8}>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleImport}
              showUploadList={false}
            >
              <Button>
                {t("requirement.quotationImportReselect")}
              </Button>
            </Upload>
            <Button onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="primary"
              disabled={rows.length === 0 || hasErrors}
              onClick={handleSubmit}
            >
              {t("requirement.next")}
            </Button>
          </Flex>
        ) : phase === "completed" ? (
          <Button type="primary" onClick={handleClose}>
            {t("common.confirm")}
          </Button>
        ) : null}
      >
        {loading ? (
          <Flex align="center" justify="center" style={{ minHeight: 300 }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <Flex vertical gap={12}>
            {importError && phase === "preview" && (
              <Alert type="error" showIcon message={importError} />
            )}
            {hasErrors && phase === "preview" && (
              <Alert
                type="error"
                showIcon
                message={t("requirement.quotationImportHasErrors")}
              />
            )}
            {phase !== "preview" && (
              <>
                <Progress
                  percent={progress}
                  status={phase === "submitting" ? "active" : "normal"}
                />
                {phase === "completed" && (
                  <Alert
                    type={summary.failed > 0 ? "warning" : "success"}
                    showIcon
                    message={t("requirement.quotationImportSummary", summary)}
                  />
                )}
              </>
            )}
            {phase !== "submitting" && (
              <CommonTable
                rowKey="key"
                columns={columns}
                dataSource={rows}
                pagination={false}
                virtual
                bordered={false}
                size="small"
                scroll={{ x: 1250, y: 520 }}
              />
            )}
          </Flex>
        )}
      </Modal>
    </>
  );
};

export default ImportQuotationExcel;
